<script setup lang="ts">
  import { useSdk } from '~/sdk';
  import { getCmsImage } from '@/composables/useCms';
  import { handleBlogCmsError, useBlog } from '@/composables/useBlog';
  import { useBlogPaths } from '@/composables/useBlogPaths';

  const { locale, t } = useI18n();
  const { getArticlePath, getTermPath } = useBlogPaths();

  const archiveRequest = useAsyncData(computed(() => `blogarchive-${locale.value}`), async () => {
    return useSdk().cms.getContent({
      entry: {
        section: 'blogArchive',
        slug: 'blog-archive',
      },
    });
  });

  const categoriesRequest = useAsyncData(computed(() => `blogcategories-${locale.value}`), async () => {
    return useSdk().cms.getContent({
      entries: {
        section: 'blogCategory',
        level: false,
        fragment: `id title slug uri`,
      },
    });
  });

  const blogRequest = useBlog();

  const [
    { data, error: archiveError },
    { data: categories, error: categoriesError },
    blog,
  ] = await Promise.all([archiveRequest, categoriesRequest, blogRequest]);

  handleBlogCmsError(computed(() => archiveError.value ?? categoriesError.value));

  useSeoMeta({
    title: () => data.value?.seo?.title,
    description: () => data.value?.seo?.description,
  });

  const introTextBlockIndex = computed(() =>
    data.value?.components?.findIndex((component: any) => component.__typename === 'text-block') ?? -1,
  );
  const textBlock = computed(() => {
    const index = introTextBlockIndex.value;

    return index >= 0 ? data.value?.components?.[index] : null;
  });
  const blocks = computed(() =>
    data.value?.components?.filter((_: any, index: number) => index !== introTextBlockIndex.value) ?? [],
  );

  const formatDate = (date: string | null) => date
    ? new Date(date).toLocaleDateString(locale.value, { day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  const {
    currentPage,
    first: firstArticle,
    hasPagination,
    data: articles,
    isFirstPage,
    totalItems,
    loading,
    pageSize,
  } = blog;
</script>

<template>
  <div class="py-16">
    <VContainer>
      <h1 v-if="isFirstPage" class="mt-8 mb-4 font-bold text-2xl border-b pb-1">Blog</h1>

      <div v-if="textBlock && isFirstPage" class="mb-8">
        <h2 class="text-xl font-semibold mb-2">{{ textBlock.heading }}</h2>
        <div v-html="textBlock.body"></div>
      </div>

      <!-- Featured article -->
      <NuxtLink
        v-if="firstArticle && isFirstPage"
        :to="getArticlePath(firstArticle)"
        class="block relative rounded overflow-hidden mb-10 group text-white"
      >
        <NuxtImg
          v-if="firstArticle.image?.[0]?.url"
          :src="getCmsImage(firstArticle.image)"
          :alt="firstArticle.image[0].title ?? firstArticle.title ?? ''"
          class="w-full object-cover max-h-96"
        />
        <div class="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-6">
          <div class="flex flex-wrap gap-1 mb-3">
            <span
              v-for="tag in firstArticle.tags"
              :key="tag.id"
              class="inline-block bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full"
            >
              {{ tag.title }}
            </span>
          </div>
          <h2 class="text-white text-2xl font-bold mb-1">{{ firstArticle.title }}</h2>
          <div v-if="firstArticle.shortDescription?.html" class="text-white/80 text-sm mb-3" v-html="firstArticle.shortDescription.html"></div>
          <div class="flex items-center gap-2 text-white/80 text-sm">
            <NuxtImg
              v-if="firstArticle.authorBlog?.[0]?.image?.[0]?.url"
              :src="getCmsImage(firstArticle.authorBlog[0].image)"
              :alt="firstArticle.authorBlog[0].image[0].title ?? firstArticle.authorBlog[0].title ?? ''"
              class="w-7 h-7 rounded-full object-cover"
            />
            <span v-if="firstArticle.authorBlog?.[0]?.title">{{ firstArticle.authorBlog[0].title }}</span>
            <span>{{ formatDate(firstArticle.postDate) }}</span>
          </div>
        </div>
      </NuxtLink>

      <!-- Featured categories -->
      <div v-if="categories && categories.length > 0 && isFirstPage" class="mb-10">
        <h2 class="text-lg font-semibold mb-3">{{ t('blog.Featured categories') }}</h2>
        <div class="flex flex-wrap gap-2">
          <NuxtLink
            v-for="category in categories"
            :key="category.slug"
            :to="getTermPath(category)"
            class="inline-block bg-primary text-white text-sm font-semibold px-4 py-1.5 rounded-full"
          >
            {{ category.title }}
          </NuxtLink>
        </div>
      </div>

      <!-- Griglia articoli -->
        <div v-if="!loading" :class="{ 'mt-8': !isFirstPage }">
          <div v-if="articles.length > 0" class="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            <BlogCard v-for="article in articles" :key="article.id" :article="article" />
          </div>
        </div>
        <div v-else class="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3" :class="{ 'mt-8': !isFirstPage }">
          <div v-for="i in pageSize" :key="i" class="border border-neutral-200 rounded-md hover:shadow-lg h-full gap-2">
            <Skeleton class="w-full h-64 rounded-sm animate-pulse" />
            <div class="flex flex-col justify-between gap-2 p-4">
              <Skeleton class="w-full h-4 rounded-sm animate-pulse" />
              <Skeleton class="w-full h-4 rounded-sm animate-pulse" />
            </div>
          </div>
        </div>

      <PaginationQuery
        v-if="hasPagination"
        :current-page="currentPage"
        :total-items="totalItems"
        :page-size="pageSize"
      />

    </VContainer>

    <!-- CMS blocks (e.g. featured product) — blocks bring their own container -->
    <div v-if="isFirstPage && blocks.length" class="mt-16 space-y-16">
      <BlockRenderer :components="blocks" :first-heading="false" />
    </div>
  </div>
</template>
